<template>
    <v-dialog :value="value" max-width="700" @input="$emit('close')">
        <v-card>
            <v-card-title class="text-h5 grey lighten-2">{{ $t('link_document') }}</v-card-title>
            <BaseRemoteList
                :ref="`documentList`"
                :session-key="`${context}-document-select-list`"
                :collection-class="collectionClass"
                :headers="headers"
                :filters="filters"
                :height="400"
                :new-btn="false"
                dense
                hide-top-toolbar
                hide-default-footer
                disable-pagination
                v-bind="$attrs"
                @edit-item="editRecord"
                v-on="$listeners"
            >
                <template #toolbar-start>
                    <FileUploadField
                        ref="fileUploadField"
                        :show-input="false"
                        :tooltip="$t('upload_document')"
                        :value="fileValue"
                        @input="fileChange"
                    />
                </template>
                <template #additional-actions="{ item }">
                    <IconBtn
                        icon-cls="download"
                        icon-color="grey"
                        :tooltip="$t('download')"
                        @click="downloadFile(item)"
                    />
                    <IconBtn
                        icon-cls="allincpast"
                        icon-color="grey"
                        :tooltip="$t('link_document')"
                        @click="$emit('select', item)"
                    />
                </template>
            </BaseRemoteList>
        </v-card>
        <EditDialog v-model="editDialog" :record="editedRecord" @saved="save" @cancelled="cancel" />
    </v-dialog>
</template>

<script>
import { MagazinDocument, MagazinDocumentCollection } from '@/models/MagazinDocument';
import { ServiceDocument, ServiceDocumentCollection } from '@/models/ServiceDocument';
import { Service } from '@/models/Service';
import { Magazin } from '@/models/Magazin';
import FileUploadField from '@/components/FileUploadField';
import { instance as api } from '@/helpers/api';
import { apiUrl } from '@/helpers/globals';
import { errToStr } from '@/helpers/tools';
import EditDialog from '@/components/TinyMceDocumentEditDialog.vue';

export default {
    name: 'TinyMceSelectDocumentDialog',
    components: {
        FileUploadField,
        EditDialog,
    },

    props: {
        value: {
            type: Boolean,
            default: false,
        },
        record: {
            type: [Magazin, Service],
            required: true,
            default: null,
        },
        context: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            headers: [
                { text: 'Aktionen', sortable: false, width: 180, value: 'actions' },
                { text: this.$t('name'), sortable: true, value: '$.name' },
                { text: this.$t('given_name'), sortable: true, value: '$.given_name' },
            ],

            editDialog: false,
            editedRecord: this.createNewDocumentRecord(),
            fileValue: null,
            isLoaded: false,
        };
    },

    computed: {
        filters() {
            return [
                {
                    property: this.getContextConfig().foreignKeyName,
                    value: this.record ? this.record.id : -1,
                },
            ];
        },

        collectionClass() {
            return this.getContextConfig().collectionClass;
        },
    },

    methods: {
        getContextConfig() {
            let contextOpts = {};
            switch (this.context) {
                case 'magazin':
                    contextOpts = {
                        documentClass: MagazinDocument,
                        collectionClass: MagazinDocumentCollection,
                        assignDocApiUrl: 'MagazinDocument/assignDocuments',
                        foreignKeyName: 'magazin_id',
                    };
                    break;
                case 'service':
                    contextOpts = {
                        documentClass: ServiceDocument,
                        collectionClass: ServiceDocumentCollection,
                        assignDocApiUrl: 'ServiceDocument/assignDocuments',
                        foreignKeyName: 'service_id',
                    };
                    break;

                default:
                    throw new Error('No context given. Supported: magazin, service');
            }
            return contextOpts;
        },

        editRecord(record) {
            this.editedRecord = record;
            this.editDialog = true;
        },

        createNewDocumentRecord() {
            let docClass = this.getContextConfig().documentClass;
            return new docClass();
        },

        async save(record) {
            if (record && record.changed()) {
                try {
                    await record.save();
                    this.reload();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                    record.reset();
                }
            }
        },

        cancel(record) {
            record.reset();
        },

        async reload() {
            await this.$refs.documentList.reload();
        },

        async fileChange(id) {
            this.$refs.fileUploadField?.clear();
            let contextOpts = this.getContextConfig();
            if (!id) {
                return;
            }
            const url = contextOpts.assignDocApiUrl;
            try {
                await api().post(url, {
                    document_ids: [id],
                    [contextOpts.foreignKeyName]: this.record.id,
                });
                this.reload();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        downloadFile(record) {
            if (!record) {
                return;
            }
            const url = apiUrl('Document/download', { document: record.document_id });
            window.open(url, record.name);
        },
    },
};
</script>
